/**
 * Reusable sortable list component using @dnd-kit
 */

import { useState, useCallback, type ReactNode } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToVerticalAxis,
  restrictToFirstScrollableAncestor,
} from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';

// Hook for creating sortable sensors with consistent config
export function useSortableSensors() {
  return useSensors(
    // TouchSensor for mobile - lower delay for responsive feel
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100, // Short delay to distinguish from scroll
        tolerance: 5, // Small tolerance for finger movement
      },
    }),
    // PointerSensor for mouse/trackpad
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
}

// Props for individual sortable items (no handle)
export interface SortableItemProps {
  id: string;
  children: ReactNode;
  className?: string;
}

// Props for sortable items with drag handle
export interface SortableItemWithHandleProps {
  id: string;
  children: ReactNode | ((props: { handleProps: object }) => ReactNode);
  className?: string;
}

// Wrapper component for sortable items
export function SortableItem({
  id,
  children,
  className,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(className, isDragging && 'z-50')}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

// Sortable item with separate drag handle
export function SortableItemWithHandle({
  id,
  children,
  className,
}: SortableItemWithHandleProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(className, isDragging && 'z-50')}
    >
      {typeof children === 'function'
        ? children({ handleProps: { ...attributes, ...listeners } })
        : children}
    </div>
  );
}

// Props for the sortable list container
export interface SortableListProps<T extends { id: string }> {
  items: T[];
  onReorder: (items: T[]) => void;
  renderItem: (item: T, index: number) => ReactNode;
  renderOverlay?: (item: T, index: number) => ReactNode;
  className?: string;
  itemClassName?: string;
  useModifiers?: boolean;
  layout?: 'vertical' | 'grid';
}

// Main sortable list component
export function SortableList<T extends { id: string }>({
  items,
  onReorder,
  renderItem,
  renderOverlay,
  className,
  useModifiers = true,
  layout = 'vertical',
}: SortableListProps<T>) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSortableSensors();

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (over && active.id !== over.id) {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        onReorder(arrayMove(items, oldIndex, newIndex));
      }
    },
    [items, onReorder]
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const activeItem = activeId ? items.find((item) => item.id === activeId) : null;
  const activeIndex = activeId ? items.findIndex((item) => item.id === activeId) : -1;

  // Only use vertical modifiers for vertical layout
  const modifiers = useModifiers && layout === 'vertical'
    ? [restrictToVerticalAxis, restrictToFirstScrollableAncestor]
    : useModifiers
      ? [restrictToFirstScrollableAncestor]
      : [];

  const strategy = layout === 'grid' ? rectSortingStrategy : verticalListSortingStrategy;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={modifiers}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={items.map((item) => item.id)}
        strategy={strategy}
      >
        <div className={className}>
          {items.map((item, index) => renderItem(item, index))}
        </div>
      </SortableContext>

      <DragOverlay
        dropAnimation={{
          duration: 250,
          easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)', // Bouncy spring feel
        }}
      >
        {activeItem && renderOverlay
          ? renderOverlay(activeItem, activeIndex)
          : activeItem
            ? renderItem(activeItem, activeIndex)
            : null}
      </DragOverlay>
    </DndContext>
  );
}

// Export hook for custom implementations
export { useSortable, CSS };
export type { DragEndEvent, DragStartEvent };
